/**
 * gRPC server interceptor: verifies the `Bearer` token in the incoming
 * `authorization` metadata header and attaches an `S2SAuthContext` to
 * the call.
 *
 * Lifted 1:1 from provisioning's
 * `server/grpc/interceptors/auth/server/auth-server-interceptor.ts`.
 */
import {
  type Interceptor,
  type InterceptorOptions,
  InterceptingCall,
  Metadata,
  status,
} from "@grpc/grpc-js";
import type { S2SAuthContext } from "../types/auth-context";
import { verifyS2SToken } from "../verify/s2s-verify";
import { grpcStatusForVerifyError } from "./grpc-error";

export type ServerAuthOptions = {
  expectedAudience: string;
  expectedIssuer: string;
};

/**
 * Wiring:
 *
 * ```ts
 * const server = new grpc.Server({
 *   interceptors: [
 *     createServerAuthInterceptor({
 *       expectedIssuer: "nucleus-s2s-auth",
 *       expectedAudience: "nucleus-internal",
 *     }),
 *   ],
 * });
 *
 * server.addService(
 *   MyServiceDefinition,
 *   wrapUnaryServiceHandlers(new MyServiceImpl(), withLoggingUnary),
 * );
 * ```
 */
export function createServerAuthInterceptor(
  opts: ServerAuthOptions,
): Interceptor {
  return (options: InterceptorOptions, nextCall) => {
    const call = nextCall(options);

    return new InterceptingCall(call, {
      async start(metadata, listener, next) {
        const authHeader = metadata.get("authorization")[0];

        if (
          typeof authHeader !== "string" ||
          !authHeader.startsWith("Bearer ")
        ) {
          listener.onReceiveStatus?.({
            code: status.UNAUTHENTICATED,
            details: "Missing Authorization header",
            metadata: new Metadata(),
          });
          return;
        }

        const token = authHeader.slice("Bearer ".length);

        try {
          const verified = await verifyS2SToken(token, {
            expectedAudience: opts.expectedAudience,
            expectedIssuer: opts.expectedIssuer,
          });

          // Attach auth context to the call. grpc-js's call object has no
          // `auth` slot in its type; the interceptor mutates it in place so
          // downstream handlers can read `call.auth`.
          (call as { auth?: S2SAuthContext }).auth = {
            serviceId: verified.serviceId,
            serviceName: verified.serviceName,
            scopes: verified.scopes,
            instanceId: verified.instanceId,
          } satisfies S2SAuthContext;

          next(metadata, listener);
        } catch (err) {
          // Routed through the shared mapper rather than deciding the status
          // locally — the defect being fixed was that each TS entry point chose
          // its own and they disagreed.
          //
          // ⚠️ On THIS path the outage arm is currently INERT: this interceptor
          // calls `verifyS2SToken` WITHOUT a replayStore, so
          // `S2SReplayStoreUnavailable` is structurally unreachable here. The
          // mapper is used anyway so that wiring a store later cannot silently
          // reintroduce the flattening. Do not read this as "the outage is
          // handled here" — the live producer is `withAuthUnaryValue`, which is
          // where the end-to-end test drives it.
          listener.onReceiveStatus?.({
            code: grpcStatusForVerifyError(err),
            details: err instanceof Error ? err.message : "Invalid token",
            metadata: new Metadata(),
          });
        }
      },
    });
  };
}
